/**
 * @fileoverview Detail Report Panel
 * @description Renders one embedded Scout detail report the way the consolidated HTML
 * report does: a titled header with a way back to the lead tab, the metric strip, the
 * summary highlights, and the full row-level results. When a comparison is supplied the
 * metric strip shows run-over-run movement instead of a single score.
 * @module features/ScoutEval/components/DetailPanel
 */

import React from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowLeft, Layers } from 'lucide-react';
import { DeltaIndicator, ScoreChip } from './common';
import { DetailEventTable } from './DetailEventTable';
import { DetailCards } from './DetailCards';

/**
 * Render a single-run metric tile.
 * @param {Object} props - Parsed detail metric.
 * @returns {JSX.Element} Metric tile.
 */
function ScoreTile({ metric }) {
  return (
    <Box className="scout-detail-metric">
      <Typography className="scout-detail-metric-label" title={metric.label}>{metric.label}</Typography>
      <ScoreChip score={metric.score} band={metric.band} label={metric.value} />
      {metric.note && <Typography className="scout-detail-metric-note">{metric.note}</Typography>}
    </Box>
  );
}

/**
 * Render a run-over-run metric tile.
 * @param {Object} props - Compared detail metric.
 * @returns {JSX.Element} Movement tile.
 */
function MovementTile({ metric }) {
  return (
    <Box className="scout-detail-metric">
      <Typography className="scout-detail-metric-label" title={metric.label}>{metric.label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography className="scout-compare-before">{metric.before?.toFixed(2) ?? '—'}</Typography>
        <Typography className="scout-compare-arrow">→</Typography>
        <Typography className="scout-compare-after">{metric.after?.toFixed(2) ?? '—'}</Typography>
      </Stack>
      <DeltaIndicator delta={metric.delta} />
    </Box>
  );
}

/**
 * Render the row-level results of a detail report.
 * @param {Object} props - Parsed content and its loading state.
 * @returns {JSX.Element} Detail content.
 */
function DetailContent({ content }) {
  if (content === undefined) {
    return (
      <Box className="scout-no-results">
        <CircularProgress size={22} />
        <Typography color="text.secondary" mt={1.5}>Loading the detail report…</Typography>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box className="scout-no-results">
        <Typography color="text.secondary">
          The row-level results for this report are no longer stored in this browser. Re-import the report to restore them.
        </Typography>
      </Box>
    );
  }

  return content.layout === 'rows'
    ? <DetailEventTable content={content} />
    : <DetailCards content={content} />;
}

/**
 * Render one embedded detail report.
 * @param {Object} props - Detail group, its row-level content, optional compared metrics,
 * back label, and back handler.
 * @returns {JSX.Element} Detail panel.
 */
export function DetailPanel({ group, content, comparison, backLabel = 'Back to summary', onBack }) {
  const metrics = comparison ? comparison.metrics : group.metrics;
  const Tile = comparison ? MovementTile : ScoreTile;

  return (
    <Paper className="scout-panel" elevation={0}>
      <Box className="scout-detail-header">
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box className="scout-group-icon"><Layers size={18} /></Box>
          <Box minWidth={0}>
            <Typography variant="h6" className="scout-detail-title">{group.label}</Typography>
            <Typography className="scout-detail-description">
              {group.description || group.title || 'Embedded detail report'}
            </Typography>
          </Box>
        </Stack>
        <Button size="small" variant="outlined" startIcon={<ArrowLeft size={15} />} onClick={onBack}>
          {backLabel}
        </Button>
      </Box>

      <Box className="scout-detail-body">
        {(group.chips?.length > 0 || group.rowCount !== null) && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap className="scout-detail-chips">
            {group.rowCount !== null && group.rowCount !== undefined && (
              <Chip size="small" variant="outlined" label={`${group.rowCount} rows evaluated`} />
            )}
            {(group.chips || []).map((chip) => (
              <Chip key={chip} size="small" variant="outlined" label={chip} />
            ))}
          </Stack>
        )}

        {metrics.length > 0 && (
          <Box className="scout-detail-metrics">
            {metrics.map((metric) => <Tile key={metric.label} metric={metric} />)}
          </Box>
        )}

        {!comparison && group.highlights?.length > 0 && (
          <Box className="scout-group-highlights">
            <Typography className="scout-metric-label">Summary highlights</Typography>
            <ul>
              {group.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
          </Box>
        )}
      </Box>

      <DetailContent content={content} />
    </Paper>
  );
}

export default DetailPanel;
